export type BookingUser = {
  userId: string;
  firstName: string;
  lastName: string;
  title: string | null;
  therapists: { therapistId: string; branchId: string }[];
};

export const getBookingTherapistIds = (user: BookingUser | undefined, branchIds: string[]) =>
  Array.from(
    new Set(
      (user?.therapists ?? [])
        .filter((assignment) => !branchIds.length || branchIds.includes(assignment.branchId))
        .map((assignment) => assignment.therapistId),
    ),
  );

export const getBookingUserName = (user: BookingUser) => `${user.firstName} ${user.lastName}`;

// The city scope always includes every branch in that city, even after the
// selected specialist is cleared. Assignment filtering is a separate step.
export const getBookingBranchIds = (branches: { id: string; city: string }[], city: string) =>
  city ? branches.filter((branch) => branch.city === city).map((branch) => branch.id) : [];

export const getBookingCities = (branches: { city: string }[]) =>
  Array.from(
    new Set(branches.map((branch) => branch.city).filter((city) => city?.trim() && city.toLowerCase() !== "online")),
  ).sort((a, b) => a.localeCompare(b));

export const getLegacyBookingCity = (branches: { id: string; city: string }[], branchIds: string[]) => {
  const cities = getBookingCities(branches.filter((branch) => branchIds.includes(branch.id)));
  return cities.length === 1 ? cities[0] : "";
};

type BookingSelection = { selectedCity?: string; selectedUserId?: string; therapist: string; branchIds: string[] };
type BookingBranch = { id: string; city: string };

export const selectBookingCity = <T extends BookingSelection>(
  state: T,
  city: string,
  branches: BookingBranch[],
  users?: BookingUser[],
): T => {
  const branchIds = getBookingBranchIds(branches, city);
  const user = users?.find((entry) => entry.userId === state.selectedUserId);
  // Loading or failed catalog data must never remove a specialist filter.
  const incompatible = city && user && !getBookingTherapistIds(user, branchIds).length;
  return { ...state, selectedCity: city, branchIds, ...(incompatible ? { selectedUserId: "", therapist: "" } : {}) };
};

export const selectBookingUser = <T extends BookingSelection>(
  state: T,
  userId: string,
  branches: BookingBranch[],
  users?: BookingUser[],
): T => {
  const user = users?.find((entry) => entry.userId === userId);
  const city = state.selectedCity ?? getLegacyBookingCity(branches, state.branchIds);
  const incompatible = user && city && !getBookingTherapistIds(user, getBookingBranchIds(branches, city)).length;
  const nextCity = incompatible ? "" : city;
  return {
    ...state,
    selectedUserId: userId,
    therapist: user ? getBookingUserName(user) : "",
    selectedCity: nextCity,
    branchIds: getBookingBranchIds(branches, nextCity),
  };
};
